import React, { useState, useEffect } from 'react'
import Papa from 'papaparse'
import { Link } from 'react-router'
import { Box, Stack, Heading, Text, Button, SimpleTable, EmptyBox, ConfirmButton, Icon, Checkbox, Menu, MenuItem, SubMenu, Link as ChakraLink, Tag, Wrap, WrapItem, ColouredTag } from '@/components/lib'
import { FaTrash, FaEdit, FaChevronDown, FaTimes } from 'react-icons/fa'
import api from '../../../api'
import utils from '../../../utils/utils'
import useStore from '../../../store'

import PackForm from './PackForm'
import PackTypeForm from './PackTypeForm'
import CSVChooser from '@/components/includes/CSVChooser'

export default function PackList ({ trialId, packType, onEditPackType, onPackTypeUpdated, onDeletePackType, canManage }) {
  const { sites = [], groups = [] } = useStore()
  const [packs, setPacks] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedPackIds, setSelectedPackIds] = useState([])
  const [packFormOpen, setPackFormOpen] = useState(false)
  const [editingPack, setEditingPack] = useState(null)
  const [packTypeFormOpen, setPackTypeFormOpen] = useState(false)

  useEffect(() => {
    if (!trialId || !packType?._id) return
    setLoading(true)
    setSelectedPackIds([])
    api.packs.getPacksForTrial(trialId, packType._id, (d) => {
      setPacks(d.packs || [])
      setLoading(false)
    }, () => setLoading(false))
  }, [trialId, packType?._id])

  function onPackCreated (pack) {
    setPacks((prev) => [pack, ...prev])
    setPackFormOpen(false)
  }

  function onPackUpdated (pack) {
    setPacks((prev) => prev.map((p) => (p._id === pack._id ? pack : p)))
    setEditingPack(null)
  }

  function onPackDeleted (packId) {
    setPacks((prev) => prev.filter((p) => p._id !== packId))
  }

  function deletePackType () {
    api.packs.deletePackType(packType._id, () => {
      utils.toast('success', 'Pack type deleted')
      onDeletePackType(packType._id)
    }, (err) => utils.toast('error', err.message))
  }

  function bulkDelete () {
    api.packs.deleteBulk(selectedPackIds, () => {
      selectedPackIds.forEach((id) => onPackDeleted(id))
      setSelectedPackIds([])
      utils.toast('success', 'Selected packs deleted')
    }, (err) => {
      utils.toast('error', err.message)
    })
  }

  function refetchPacks () {
    api.packs.getPacksForTrial(trialId, packType._id, (d) => {
      setPacks(d.packs || [])
    })
  }

  function bulkAssignToSite (siteId) {
    api.packs.updateBulk(
      selectedPackIds,
      { assignedToSite: siteId || null },
      () => {
        refetchPacks()
        utils.toast('success', 'Selected packs updated')
      },
      (err) => utils.toast('error', err.message)
    )
  }

  function bulkMakeAvailableToAllGroups () {
    api.packs.updateBulk(
      selectedPackIds,
      { availableToAllGroups: true, availableToGroups: [] },
      () => {
        refetchPacks()
        utils.toast('success', 'Selected packs available to entire trial')
      },
      (err) => utils.toast('error', err.message)
    )
  }

  function bulkMakeAvailableToGroup (groupId) {
    api.packs.updateBulk(
      selectedPackIds,
      { availableToAllGroups: false, availableToGroups: [groupId] },
      () => {
        refetchPacks()
        utils.toast('success', 'Selected packs updated')
      },
      (err) => utils.toast('error', err.message)
    )
  }

  function csvFormatter (text) {
    const dat = Papa.parse(text, {
      skipEmptyLines: true,
      header: true,
      transformHeader: (h) => h.toLowerCase().replace(/\s+/g, ''),
      transform: (v) => (v != null ? String(v).trim() : '')
    })
    const header = ['name', 'batchNumber']
    const rows = (dat.data || []).map((row) => {
      const name = (row.name ?? '').trim() || null
      const batchNumber = (row.batchnumber ?? row.batchNumber ?? '').trim()
      return { name, batchNumber }
    })
    const seen = new Set()
    const withErrors = rows.map((r) => {
      const out = { ...r }
      if (!r.batchNumber) {
        out.error = 'Batch number is required'
      } else if (seen.has(r.batchNumber)) {
        out.error = 'Duplicate batch number'
      } else {
        seen.add(r.batchNumber)
      }
      return out
    })
    return [header, withErrors]
  }

  function bulkCreatePacks (rows) {
    const packs = rows.filter((r) => !r.error).map((r) => ({ name: r.name || null, batchNumber: r.batchNumber }))
    if (!packs.length) return
    api.packs.createBulk(trialId, packType._id, packs, (res) => {
      setPacks((prev) => [...(res.packs || []), ...prev])
      utils.toast('success', 'Imported successfully')
    }, (err) => utils.toast('error', err.message))
  }

  function toggleSelected (packId) {
    setSelectedPackIds((prev) =>
      prev.includes(packId) ? prev.filter((id) => id !== packId) : [...prev, packId]
    )
  }

  function unassignPackFromParticipant (pack) {
    api.participants.unassignPack(trialId, pack.participant, pack._id, (d) => {
      refetchPacks()
      utils.toast('success', 'Pack unassigned')
    }, (err) => utils.toast('error', err.message))
  }

  const baseHeaders = ['Name', 'Batch', 'Site', 'Groups', 'Participant', canManage ? ' ' : null].filter(Boolean)
  const headers = canManage
    ? [
      <Menu
        key='bulk'
        closeOnSelect={false}
        trigger={<Button size='xs' variant='ghost'>{selectedPackIds.length || 0} <Icon as={FaChevronDown} /></Button>}
      >
        <MenuItem value='none' onClick={() => setSelectedPackIds([])}>Deselect all</MenuItem>
        <MenuItem value='all' onClick={() => setSelectedPackIds(packs.map((p) => p._id))}>Select all</MenuItem>
        {selectedPackIds.length > 0 && (
          <>
            <hr />
            <SubMenu trigger={<Box>Assign to site</Box>}>
              <MenuItem value='none' onClick={() => bulkAssignToSite(null)}>No site</MenuItem>
              {sites.length > 0 && <hr />}
              {sites.map((s) => (
                <MenuItem key={s._id} value={s._id} onClick={() => bulkAssignToSite(s._id)}>
                  {s.name || s.id || s._id}
                </MenuItem>
              ))}
            </SubMenu>
            <SubMenu trigger={<Box>Make available to</Box>}>
              <MenuItem value='trial' onClick={bulkMakeAvailableToAllGroups}>Any group</MenuItem>
              {groups.length > 0 && <hr />}
              {groups.map((g) => (
                <MenuItem key={g._id} value={g._id} onClick={() => bulkMakeAvailableToGroup(g._id)}>
                  {g.name || g._id}
                </MenuItem>
              ))}
            </SubMenu>
            <ConfirmButton
              trigger={<MenuItem value='delete'>Delete selected packs</MenuItem>}
              header='Really delete the selected packs?'
              onConfirm={bulkDelete}
              actionColor='red'
            />
          </>
        )}
      </Menu>,
      ...baseHeaders
      ]
    : baseHeaders

  if (loading) return <Box textAlign='center'><Text>Loading packs…</Text></Box>

  return (
    <Stack gap={5}>
      <Box>
        <Heading as='h3' size='md'>{packType.name}</Heading>
        {packType.description && <Text fontSize='sm' color='fg.muted'>{packType.description}</Text>}
      </Box>
      <Stack direction='row' justifyContent='end' gap={2}>
        {canManage && (
          <>
            <Button size='sm' colorPalette='primary' onClick={() => setPackFormOpen(true)}>
              Create pack
            </Button>
            <CSVChooser
              trigger={<Button size='sm' colorPalette='primary' variant='outline'>Bulk create packs</Button>}
              title='Bulk create packs for this pack type'
              instructions='Upload a CSV file with "name" and "batchNumber" columns (or "batch number"). Batch numbers must be unique for this pack type.'
              sample={{ filename: 'packs_sample.csv', headings: ['name', 'batchNumber'], rows: [['Medication pack A', 'BATCH-001'], ['Medication pack B', 'BATCH-002'], ['Medication pack C', 'BATCH-003']] }}
              formatFunction={csvFormatter}
              onComplete={bulkCreatePacks}
            />
            <Button size='sm' variant='outline' leftIcon={<Icon as={FaEdit} />} onClick={() => setPackTypeFormOpen(true)}>
              Edit type
            </Button>
            <ConfirmButton
              trigger={<Button size='sm' variant='outline' colorPalette='red' leftIcon={<Icon as={FaTrash} />}>Delete type</Button>}
              header='Delete this pack type?'
              footer='You can only delete a pack type when it has no packs.'
              onConfirm={deletePackType}
            />
          </>
        )}
      </Stack>

      {packs.length === 0
        ? (
          <EmptyBox icon='pack' title='No packs yet' description={`Create or import "${packType.name}" packs to get started.`}>
            {canManage && <Button colorPalette='primary' onClick={() => setPackFormOpen(true)}>Create pack</Button>}
          </EmptyBox>
          )
        : (
          <SimpleTable
            asCard
            tableProps={{ size: 'sm' }}
            headers={headers}
            rows={packs.map((pack) => {
              const cells = []
              if (canManage) {
                cells.push(
                  <Checkbox
                    key={pack._id}
                    isChecked={selectedPackIds.includes(pack._id)}
                    onChange={() => toggleSelected(pack._id)}
                  />
                )
              }
              const site = pack.assignedToSite ? sites.find((s) => String(s._id) === String(pack.assignedToSite)) : null
              const groupsDisplay = pack.availableToAllGroups
                ? ''
                : (pack.availableToGroups || []).length === 0
                    ? ''
                    : (
                      <Wrap gap={1}>{(pack.availableToGroups || []).map((gid) => {
                        const group = groups.find((g) => String(g._id) === String(gid))
                        return <WrapItem key={gid}><ColouredTag size='sm' key={gid} colour={group?.colour} label={group?.name || gid} /></WrapItem>
                      })}
                      </Wrap>
                      )
              const participantDisplay = pack.participantObject?.id
                ? (
                  <Stack key={pack._id} direction='row' alignItems='center' gap={1} display='inline-flex'>
                    <ChakraLink as={Link} variant='underline' colorPalette='primary' to={`/trials/${trialId}/participants/${pack.participantObject._id}`}>{pack.participantObject.id ?? pack.participantObject._id}</ChakraLink>
                    <ConfirmButton
                      trigger={<Button size='xs' variant='ghost' aria-label='Unassign pack'><Icon as={FaTimes} /></Button>}
                      header='Unassign this pack from the participant?'
                      onConfirm={() => unassignPackFromParticipant(pack)}
                    />
                  </Stack>
                  )
                : ''
              cells.push(
                <Text key={pack._id} fontWeight='bold' fontSize='xs'>{pack.name ?? ''}</Text>,
                <Text key={pack._id} fontFamily='mono' fontSize='xs'>{pack.batchNumber ?? ''}</Text>,
                site ? <Tag size='sm' key={pack._id} variant='subtle' label={site.name ?? site.id ?? ''} /> : '',
                groupsDisplay,
                participantDisplay
              )
              if (canManage) {
                cells.push(
                  <Stack key={pack._id} direction='row' justifyContent='end' gap={1}>
                    <Button size='sm' variant='outline' onClick={() => setEditingPack(pack)}>Edit</Button>
                    <ConfirmButton
                      trigger={<Button size='sm' variant='ghost'><Icon as={FaTrash} /></Button>}
                      header='Delete this pack?'
                      onConfirm={() => {
                        api.packs.deletePack(pack._id, () => {
                          utils.toast('success', 'Pack deleted')
                          onPackDeleted(pack._id)
                        }, (err) => utils.toast('error', err.message))
                      }}
                    />
                  </Stack>
                )
              }
              return cells
            })}
          />
          )}

      {packFormOpen && (
        <PackForm
          trialId={trialId}
          packType={packType}
          sites={sites}
          groups={groups}
          onClose={() => setPackFormOpen(false)}
          onCreated={onPackCreated}
        />
      )}
      {editingPack && (
        <PackForm
          trialId={trialId}
          packType={packType}
          pack={editingPack}
          sites={sites}
          groups={groups}
          onClose={() => setEditingPack(null)}
          onUpdated={onPackUpdated}
        />
      )}
      {packTypeFormOpen && (
        <PackTypeForm
          trialId={trialId}
          packType={packType}
          onClose={() => setPackTypeFormOpen(false)}
          onUpdated={(updated) => {
            setPackTypeFormOpen(false)
            onPackTypeUpdated && onPackTypeUpdated(updated)
          }}
        />
      )}
    </Stack>
  )
}
